import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery, queryOptions } from "@tanstack/react-query";
import { listNewsletterSubscribers } from "@/lib/contact.functions";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Mail, Search, Users } from "lucide-react";

export const Route = createFileRoute("/_authenticated/admin/newsletter")({
  component: NewsletterAdmin,
});

type Subscriber = {
  id: string;
  email: string;
  source: string | null;
  is_active: boolean | number;
  created_at: string;
};

const subscribersQueryOptions = () =>
  queryOptions({
    queryKey: ["newsletter_subscribers"],
    queryFn: async () => (await listNewsletterSubscribers()) as Subscriber[],
  });

const fmtDate = (iso: string) =>
  new Date(iso.replace(" ", "T") + (iso.endsWith("Z") ? "" : "Z")).toLocaleDateString(undefined, {
    dateStyle: "medium",
  });

function NewsletterAdmin() {
  const { data: rows = [], isLoading } = useQuery(subscribersQueryOptions());
  const [q, setQ] = useState("");

  const active = useMemo(() => rows.filter((s) => !!s.is_active).length, [rows]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((s) =>
      [s.email, s.source ?? ""].join(" ").toLowerCase().includes(needle),
    );
  }, [rows, q]);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Users className="h-4 w-4" /> Subscribers
            </CardDescription>
            <CardTitle className="text-3xl">{isLoading ? "—" : rows.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription className="flex items-center gap-2">
              <Mail className="h-4 w-4" /> Active
            </CardDescription>
            <CardTitle className="text-3xl">{isLoading ? "—" : active}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Newsletter subscribers</CardTitle>
          <CardDescription>
            Every signup from the website, deduplicated by email address and updated the moment
            someone subscribes. Each signup also appears under Messages.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative max-w-xs">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or source"
              className="pl-8"
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead className="hidden sm:table-cell">Source</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-36">Subscribed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <a href={`mailto:${s.email}`} className="text-primary hover:underline">
                      {s.email}
                    </a>
                  </TableCell>
                  <TableCell className="hidden text-muted-foreground sm:table-cell">
                    {s.source ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={s.is_active ? "secondary" : "outline"}>
                      {s.is_active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-muted-foreground">
                    {fmtDate(s.created_at)}
                  </TableCell>
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="py-10 text-center text-muted-foreground">
                    {isLoading
                      ? "Loading subscribers…"
                      : rows.length === 0
                        ? "No subscribers yet. Signups from the homepage newsletter form appear here automatically."
                        : "No subscribers match the search."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
